import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useUser } from "../context/UserContext";
import StopIcon from "@mui/icons-material/Stop";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/material/CircularProgress";
import { useLiveAvatarSession } from "../hooks/useLiveAvatarSession";

export default function AdvisorView() {
  // Use LiveAvatar SDK hook
  const {
    isStreamReady,
    conversations: sdkConversations,
    micUnmute,
    isAvatarTalking,
    questionLimitReached,
    initSession,
    attachVideo,
    sendMessage: sdkSendMessage,
    toggleMic: sdkToggleMic,
    interrupt: sdkInterrupt,
    endSession: sdkEndSession,
  } = useLiveAvatarSession();

  const location = useLocation();
  const { projectId } = useParams();
  const {
    user,
    surveyId,
    audienceGroupId,
    projectName,
    createdAt,
    bannerBookFiles,
    researchFiles,
    bannerImagePath,
    fullProject,
  } = location.state || {};
  const navigate = useNavigate();
  const [userInput, setUserInput] = useState("");
  const [openStartDialog, setOpenStartDialog] = useState(true);
  const [openUserProfileDialog, setOpenUserProfileDialog] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [status, setStatus] = useState("Idle");
  const [isInitializingSession, setIsInitializingSession] = useState(false);
  const [insightsList, setInsightsList] = useState([]);
  const [openEndSessionDialog, setOpenEndSessionDialog] = useState(false);
  const [endSessionTimer, setEndSessionTimer] = useState(60);
  const endSessionTimerRef = useRef(null);
  // Toast/Alert states
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");

  const bucketUrl =
    "https://generativeinsight-images-test.s3.us-east-1.amazonaws.com/";

  const videoRef = useRef(null);
  const [aiqData, setAiqData] = useState([]);

  // Use SDK conversations or fall back to empty
  const conversations =
    sdkConversations.length > 0
      ? sdkConversations
      : [
          {
            user_text: "hi",
            avatar_response: "Hey there! What's on your mind?",
          },
        ];

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isMouseInside, setIsMouseInside] = useState(false);
  const chatContainerRef = useRef(null);

  const [parmasForSession, setparmasForSession] = useState({
    session_id: "",
    access_token: "",
  });

  const [chatViewChange, setchatViewChange] = useState({
    direction: "row",
    column1: 12,
    column1H: "70vh",
    column2: 12,
    column2H: "25vh",
    bannerBgw: "96vw",
    bannerBgh: "55vh",
    leftSpace: "4vw",
    adimgleft: "40rem",
  });

  const [deviceType, setDeviceType] = useState("desktop");
  const updateStatus = (msg) => setStatus(msg);

  // Function to navigate back properly
  const navigateBack = () => {
    navigate(`/projects/view/${projectId}`, {
      state: {
        surveyId,
        audienceGroupId,
        projectName,
        createdAt,
        bannerBookFiles,
        researchFiles,
        bannerImagePath,
        fullProject,
      },
    });
  };

  const handleUserProfileClick = () => {
    setOpenUserProfileDialog(true);
  };

  const handleCloseUserProfile = () => {
    setOpenUserProfileDialog(false);
  };

  const showAlert = (message, severity = "error") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setShowQuotaAlert(true);
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setShowQuotaAlert(false);
  };

  const fetchAiqData = async (segmentName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/avatar/fetch-aiq-data`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            segment_name: segmentName,
            survey_id: surveyId,
            audience_grp: audienceGroupId,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        const parsedData = JSON.parse(data);
        setAiqData(parsedData);
      } else {
        console.error("Failed to fetch AIQ data:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching AIQ data:", error);
    }
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/avatar/segments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        survey_id: surveyId,
        audience_grp: audienceGroupId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setInsightsList(data);
      })
      .catch((err) => console.error("Error fetching insights:", err));
    if (user?.segment_name) {
      fetchAiqData(user.segment_name);
    }
  }, [user?.segment_name, surveyId, audienceGroupId]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isNearBottom);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversations]);

  // Attach video element when stream is ready AND video element exists in DOM
  useEffect(() => {
    if (isStreamReady && sessionStarted && videoRef.current) {
      attachVideo(videoRef.current);
    } else if (isStreamReady && sessionStarted && !videoRef.current) {
      console.error(
        "Stream is ready and session started but videoRef.current is still null!",
      );
    }
  }, [isStreamReady, sessionStarted, attachVideo]);

  const toggleMic = async () => {
    await sdkToggleMic();
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mounted) {
        // await initAvatar();
      }
    })();

    // Handler for page unload/navigation/refresh
    const handleBeforeUnload = (e) => {
      const currentSessionId = parmasForSession.session_id;
      if (
        currentSessionId &&
        typeof currentSessionId === "string" &&
        currentSessionId.length > 10
      ) {
        // Use sendBeacon for more reliable cleanup on page unload
        const data = JSON.stringify({ session_id: currentSessionId });
        const blob = new Blob([data], { type: "application/json" });
        navigator.sendBeacon(
          `${process.env.REACT_APP_API_URL}/avatar/end-session`,
          blob,
        );
      }
    };

    // Handler for React Router navigation (when user clicks back/forward or navigates to another route)
    const handleRouteChange = () => {
      const currentSessionId = parmasForSession.session_id;
      if (
        currentSessionId &&
        typeof currentSessionId === "string" &&
        currentSessionId.length > 10 &&
        sessionStarted
      ) {
        // End session on route change
        fetch(`${process.env.REACT_APP_API_URL}/avatar/end-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: currentSessionId }),
          keepalive: true, // Keep request alive even if page is unloading
        }).catch((err) => console.error("Route change cleanup error:", err));
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handleRouteChange); // Browser back/forward

    // Cleanup function
    return () => {
      mounted = false;

      // Remove event listeners
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handleRouteChange);

      // Clear any active timers
      if (endSessionTimerRef.current) {
        clearInterval(endSessionTimerRef.current);
      }

      // End session when component unmounts (navigating away in React Router)
      const currentSessionId = parmasForSession.session_id;
      if (
        currentSessionId &&
        typeof currentSessionId === "string" &&
        currentSessionId.length > 10 &&
        sessionStarted
      ) {
        // Use both methods for maximum reliability

        // Method 1: sendBeacon (works even as page is closing)
        const data = JSON.stringify({ session_id: currentSessionId });
        const blob = new Blob([data], { type: "application/json" });
        navigator.sendBeacon(
          `${process.env.REACT_APP_API_URL}/avatar/end-session`,
          blob,
        );

        // Method 2: fetch with keepalive (backup)
        fetch(`${process.env.REACT_APP_API_URL}/avatar/end-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: currentSessionId }),
          keepalive: true,
        }).catch((err) => console.error("Cleanup error:", err));
      }
    };
  }, [parmasForSession.session_id, sessionStarted]);
  useEffect(() => {
    const checkDevice = () => {
      if (window.innerWidth < 768) {
        setDeviceType("mobile");
        setchatViewChange({
          direction: "column",
          column1: 12,
          column1H: "auto",
          column2: 12,
          column2H: "auto",
          bannerBgw: "100%",
          bannerBgh: "40vh",
          leftSpace: "0",
          adimgleft: "1rem",
        });
      } else if (window.innerWidth >= 768 && window.innerWidth < 1024) {
        setDeviceType("tablet");
        setchatViewChange({
          direction: "row",
          column1: 12,
          column1H: "60vh",
          column2: 12,
          column2H: "30vh",
          bannerBgw: "100%",
          bannerBgh: "50vh",
          leftSpace: "0",
          adimgleft: "8rem",
        });
      } else {
        setDeviceType("desktop");
        setchatViewChange({
          direction: "row",
          column1: 12,
          column1H: "70vh",
          column2: 12,
          column2H: "25vh",
          bannerBgw: "100%",
          bannerBgh: "55vh",
          leftSpace: "0",
          adimgleft: "40rem",
        });
      }
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => {
      window.removeEventListener("resize", checkDevice);
    };
  }, []);

  const endSession = async () => {
    // End LiveAvatar SDK session (SDK handles all cleanup)
    await sdkEndSession();

    // Notify backend to cleanup
    if (parmasForSession.session_id) {
      fetch(`${process.env.REACT_APP_API_URL}/avatar/end-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: parmasForSession.session_id,
        }),
      })
        .then((res) => res.json())
        .then((response) => {
          console.log("Backend session ended", response);
        })
        .catch((err) => console.error("Error in backend session end:", err));
    }

    setSessionStarted(false);
  };

  const initAvatar = async () => {
    setIsInitializingSession(true);

    try {
      // Step 1: Create session token
      const tokenRes = await fetch(
        `${process.env.REACT_APP_API_URL}/avatar/create-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            avatar_id: user.avatar_id,
            context_id: user.kb_id,
            language: "en",
            mode: "FULL",
          }),
        },
      );

      const tokenData = await tokenRes.json();

      if (
        tokenRes.status === 429 ||
        (tokenData && tokenData.error && tokenData.error.includes("quota"))
      ) {
        showAlert(
          "Session quota has been exhausted. Please try again later.",
          "warning",
        );
        setIsInitializingSession(false);
        return;
      }

      if (tokenRes.status === 402) {
        showAlert(
          "Payment required. Please check your billing status.",
          "error",
        );
        setIsInitializingSession(false);
        return;
      }

      if (!tokenRes.ok) {
        showAlert(
          `Failed to create session: ${tokenData?.error || "Unknown error"}`,
          "error",
        );
        setIsInitializingSession(false);
        return;
      }

      const sessionToken = tokenData?.session_token;
      const sessionId = tokenData?.session_id;

      if (!sessionToken || !sessionId) {
        console.error("No session token or session_id received:", tokenData);
        showAlert("Failed to initialize session. No token received.", "error");
        setIsInitializingSession(false);
        return;
      }

      console.log("Session token created:", sessionId);

      // Step 2: Initialize LiveAvatar SDK session
      // The SDK internally connects to LiveKit and handles everything:
      // - Video/audio streaming
      // - Microphone track management
      // - Transcription events
      // Note: Video element attachment happens in useEffect when stream is ready
      await initSession(sessionToken);

      // Store session info for later use
      setparmasForSession({
        session_id: sessionId,
        session_token: sessionToken,
      });

      setSessionStarted(true);
      setOpenStartDialog(false);
      setIsInitializingSession(false);
      showAlert("Session started successfully!", "success");

      // Note: Voice chat will be started manually by user clicking mic button
      // We don't auto-start it anymore to give user control
    } catch (error) {
      console.error("Error initializing avatar:", error);

      if (error.message && error.message.includes("quota")) {
        showAlert(
          "Session quota has been exhausted. Please try again later.",
          "warning",
        );
      } else if (error.message && error.message.includes("payment")) {
        showAlert(
          "Payment required. Please check your billing status.",
          "error",
        );
      } else {
        showAlert(`Failed to initialize session: ${error.message}`, "error");
      }

      setIsInitializingSession(false);
    }
  };

  const handleEndSession = async () => {
    setOpenEndSessionDialog(true);
    setEndSessionTimer(60);

    // Start countdown timer
    endSessionTimerRef.current = setInterval(() => {
      setEndSessionTimer((prev) => {
        if (prev <= 1) {
          // Auto-end session when timer reaches 0
          confirmEndSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const confirmEndSession = async () => {
    if (endSessionTimerRef.current) {
      clearInterval(endSessionTimerRef.current);
      endSessionTimerRef.current = null;
    }
    setOpenEndSessionDialog(false);
    await endSession();
    setSessionStarted(false);
    navigateBack();
  };

  const cancelEndSession = () => {
    // Clear timer
    if (endSessionTimerRef.current) {
      clearInterval(endSessionTimerRef.current);
      endSessionTimerRef.current = null;
    }

    setOpenEndSessionDialog(false);
    setEndSessionTimer(60);
  };
  const handleCancelAndNavigate = () => {
    setOpenStartDialog(false);
    navigateBack();
  };

  const handleCloseStartDialog = () => {
    if (!isInitializingSession) {
      navigateBack();
    }
  };

  const handleSend = async () => {
    const text = userInput.trim();

    if (!text) return;

    setUserInput("");

    // Use LiveAvatar SDK to send message (no backend API needed)
    await sdkSendMessage(text);
  };

  return (
    <div
      className="dark-bg generating-Insights"
      style={{ overflow: "visible", minHeight: "125vh" }}
    >
      <Snackbar
        open={showQuotaAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{
            width: "100%",
            backgroundColor:
              alertSeverity === "error"
                ? "#dc2626"
                : alertSeverity === "warning"
                  ? "#f59e0b"
                  : "#10b981",
            color: "white",
            "& .MuiAlert-icon": {
              color: "white",
            },
            "& .MuiAlert-action": {
              color: "white",
            },
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Start Session Dialog */}
      <Dialog
        open={openStartDialog}
        onClose={handleCloseStartDialog}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#000000",
            border: "2px solid #3b82f6",
            borderRadius: "8px",
            color: "white",
            width: "500px",
            maxWidth: "90vw",
            minHeight: "auto",
            position: "relative",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            fontSize: "1.3rem",
            fontWeight: "bold",
            paddingBottom: "20px",
            marginBottom: "10px",
          }}
        >
          Good to see you!
          {!isInitializingSession && (
            <IconButton
              aria-label="close"
              onClick={handleCloseStartDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#3b82f6",
                "&:hover": {
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            padding: "15px 20px",
          }}
        >
          <Box>
            <div style={{ fontSize: "1rem", marginBottom: "8px" }}>
              Ready to start your session with
            </div>
            <div
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: "#3b82f6",
              }}
            >
              {user?.name}?
            </div>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#000000",
            justifyContent: "center",
            paddingBottom: "15px",
            gap: "12px",
          }}
        >
          <Button
            onClick={handleCancelAndNavigate}
            disabled={isInitializingSession}
            sx={{
              color: isInitializingSession ? "#333" : "#666",
              border: "1px solid #444",
              padding: "10px 30px",
              "&:hover": {
                backgroundColor: isInitializingSession
                  ? "transparent"
                  : "rgba(255, 255, 255, 0.1)",
                borderColor: isInitializingSession ? "#444" : "#666",
              },
              "&:disabled": {
                color: "#333",
                borderColor: "#444",
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={initAvatar}
            variant="contained"
            disabled={isInitializingSession}
            sx={{
              backgroundColor: isInitializingSession ? "#1e40af" : "#3b82f6",
              color: "white",
              padding: "10px 30px",
              minWidth: "140px",
              "&:hover": {
                backgroundColor: isInitializingSession ? "#1e40af" : "#2563eb",
              },
              "&:disabled": {
                backgroundColor: "#1e40af",
                color: "white",
              },
            }}
          >
            {isInitializingSession ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "white" }} />
                <span>STARTING...</span>
              </Box>
            ) : (
              "START SESSION"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Profile Dialog */}
      <Dialog
        open={openUserProfileDialog}
        className="custom-scroller"
        onClose={handleCloseUserProfile}
        maxWidth={false}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            borderRadius: "12px",
            color: "white",
            width: "600px",
            maxWidth: "90vw",
            maxHeight: "90vh",
            position: "relative",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "transparent",
            color: "white",
            textAlign: "left",
            fontSize: "1.5rem",
            fontWeight: "600",
            padding: "24px 24px 16px 24px",
            position: "relative",
          }}
        >
          {user?.name} ({user?.age})
          <IconButton
            aria-label="close"
            onClick={handleCloseUserProfile}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "#94a3b8",
              "&:hover": {
                backgroundColor: "rgba(148, 163, 184, 0.1)",
                color: "white",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: "transparent",
            color: "white",
            padding: "0 24px 24px 24px",
            overflow: "auto",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "flex-start", mb: 3, gap: 2 }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <img
                src={`${bucketUrl}${user?.avatar_id}.webp`}
                alt={user?.name}
                style={{
                  width: "180px",
                  height: "180px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div
                style={{
                  display: "none",
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: "#878E9D",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  border: "2px solid #878E9D",
                }}
              >
                {user?.name?.charAt(0) || "A"}
              </div>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: "#878E9D",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  mb: 0.5,
                }}
              >
                Location
              </Typography>
              <Typography
                sx={{ color: "#ffffff", fontSize: "0.95rem", mb: 1.5 }}
              >
                {user?.location || "Austin, Texas"}
              </Typography>

              <Typography
                sx={{
                  color: "#878E9D",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  mb: 0.5,
                }}
              >
                Occupation
              </Typography>
              <Typography
                sx={{ color: "#ffffff", fontSize: "0.95rem", mb: 1.5 }}
              >
                {user?.occupation ||
                  user?.job_title ||
                  "Marketing Manager at a mid-size tech company"}
              </Typography>

              <Typography
                sx={{
                  color: "#878E9D",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  mb: 0.5,
                }}
              >
                Education
              </Typography>
              <Typography sx={{ color: "#ffffff", fontSize: "0.95rem" }}>
                {user?.education || "Bachelor's degree in Communications"}
              </Typography>
            </Box>
          </Box>

          {aiqData.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {aiqData.map((category, categoryIndex) => (
                <Box key={categoryIndex} sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      color: "#878E9D",
                      fontSize: "1rem",
                      fontWeight: "600",
                      mb: 2,
                    }}
                  >
                    {category.type}
                  </Typography>
                  {category.attributes &&
                    category.attributes.map((attr, attrIndex) => (
                      <Box
                        key={attrIndex}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          backgroundColor: "rgba(255, 255, 255, 0.02)",
                          borderRadius: "8px",
                          border: "1px solid rgba(255, 255, 255, 0.05)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#878E9D",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                              flex: 1,
                            }}
                          >
                            {attr.attribute}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#ffffff",
                              fontSize: "0.95rem",
                              flex: 1,
                              textAlign: "right",
                              fontWeight: "600",
                            }}
                          >
                            {attr.value}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            color: "#94a3b8",
                            fontSize: "0.8rem",
                            fontStyle: "italic",
                            lineHeight: "1.4",
                            opacity: 0.9,
                          }}
                        >
                          {attr.description}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={openEndSessionDialog}
        onClose={() => {}} // Prevent closing by clicking outside
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#000000",
            border: "2px solid #1e40af",
            borderRadius: "8px",
            color: "white",
            width: "450px",
            maxWidth: "90vw",
            minHeight: "auto",
            position: "relative",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            fontSize: "1.3rem",
            fontWeight: "bold",
            paddingBottom: "10px",
          }}
        >
          End Session?
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1rem", mb: 2 }}>
              Are you sure you want to end your session with {user?.name}?
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <CircularProgress
                variant="determinate"
                value={(endSessionTimer / 60) * 100}
                size={40}
                sx={{
                  color: endSessionTimer <= 10 ? "##3b82f6" : "#3b82f6",
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: endSessionTimer <= 10 ? "#ef4444" : "#94a3b8",
                }}
              >
                Auto-ending in {endSessionTimer}s
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#000000",
            justifyContent: "center",
            paddingBottom: "20px",
            gap: "12px",
          }}
        >
          <Button
            onClick={cancelEndSession}
            sx={{
              color: "#666",
              border: "1px solid #444",
              padding: "10px 30px",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderColor: "#666",
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={confirmEndSession}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              padding: "10px 30px",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
            }}
          >
            END SESSION
          </Button>
        </DialogActions>
      </Dialog>
      {sessionStarted && (
        <IconButton
          onClick={handleEndSession}
          sx={{
            position: "absolute",
            top: "20px",
            right: "20px",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            zIndex: 1000,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          }}
          size="medium"
        >
          <CloseIcon />
        </IconButton>
      )}

      <Box
        className="advisor-view-web"
        sx={{
          paddingLeft: { xs: 0, md: "65px" },
          width: "100%",
          maxWidth: "100vw",
          overflow: "hidden",
        }}
      >
        <Grid
          container
          spacing={2}
          direction={chatViewChange.direction}
          sx={{
            justifyContent: "center",
            display: "flex",
            alignItems: "baseline",
            width: "100%",
            margin: 0,
            "& .MuiGrid-item": {
              paddingLeft: { xs: "8px", sm: "16px" },
              paddingTop: { xs: "8px", sm: "16px" },
            },
          }}
        >
          <Grid
            size={chatViewChange.column1}
            sx={{
              height: chatViewChange.column1H,
              position: "relative",
              width: "100%",
            }}
          >
            <Box className="advisor-view">
              <Box
                className="banner-bg"
                sx={{
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  left: { xs: "0", md: chatViewChange.leftSpace },
                  width: { xs: "100%", md: chatViewChange.bannerBgw },
                  height: chatViewChange.bannerBgh,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: { xs: "0 auto", md: "0" },
                  maxWidth: "100%",
                }}
              >
                {!sessionStarted && (
                  <img
                    src={`${bucketUrl}${user?.avatar_id}.webp`}
                    alt={user?.name}
                    style={{
                      width:
                        deviceType === "mobile"
                          ? "280px"
                          : deviceType === "tablet"
                            ? "350px"
                            : "450px",
                      height:
                        deviceType === "mobile"
                          ? "280px"
                          : deviceType === "tablet"
                            ? "350px"
                            : "450px",
                      borderRadius: "12px",
                      objectFit: "cover",
                      maxWidth: "100%",
                      maxHeight: "100%",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                )}

                {!sessionStarted && (
                  <div
                    style={{
                      display: "none",
                      width:
                        deviceType === "mobile"
                          ? "280px"
                          : deviceType === "tablet"
                            ? "350px"
                            : "450px",
                      height:
                        deviceType === "mobile"
                          ? "280px"
                          : deviceType === "tablet"
                            ? "350px"
                            : "450px",
                      borderRadius: "12px",
                      backgroundColor: "#878E9D",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontWeight: "bold",
                      fontSize: "4rem",
                      maxWidth: "100%",
                      maxHeight: "100%",
                    }}
                  >
                    {user?.name?.charAt(0) || "A"}
                  </div>
                )}

                {sessionStarted && (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                      width:
                        deviceType === "mobile"
                          ? "280px"
                          : deviceType === "tablet"
                            ? "400px"
                            : "600px",
                      height:
                        deviceType === "mobile"
                          ? "210px"
                          : deviceType === "tablet"
                            ? "300px"
                            : "450px",
                      borderRadius: "12px",
                      objectFit: "cover",
                      maxWidth: "100%",
                      maxHeight: "100%",
                    }}
                  />
                )}
              </Box>

              <Box
                className={`cardBox advisor-info web-view-only ${
                  sessionStarted ? "session-active" : ""
                }`}
                sx={{
                  position: "absolute",
                  top: "1rem",
                  right: { xs: "0.5rem", md: "1rem" },
                  zIndex: 10,
                  width: { xs: "280px", sm: "350px", md: "400px" },
                  maxWidth: { xs: "calc(100% - 1rem)", md: "400px" },
                  backgroundColor: "rgba(16, 17, 21, 0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box textAlign="left">
                    <div className="insight-name">
                      {user?.name} ({user?.age})
                    </div>
                    <span>{user?.segment_name}</span>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={handleUserProfileClick}
                      sx={{
                        color: "white",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                    >
                      <EastIcon sx={{ marginTop: 1 }} />
                    </IconButton>
                  </Box>
                </Stack>
                <hr />
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <span>Occupation</span>
                    <p className="white-text m-0">{user?.occupation}</p>
                  </Grid>
                  <Grid size={6}>
                    <span>Education</span>
                    <p className="white-text m-0">{user?.education}</p>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>

          <Box
            className="cardBox advisor-info mobile-view-only"
            sx={{
              display: { xs: "block", md: "none" },
              margin: "1rem",
              width: "calc(100% - 2rem)",
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box textAlign="left">
                <div className="insight-name">
                  {user?.name} ({user?.age})
                </div>
                <span>{user?.segment_name}</span>
              </Box>
              <Box>
                <IconButton
                  onClick={handleUserProfileClick}
                  sx={{
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <EastIcon sx={{ marginTop: 1 }} />
                </IconButton>
              </Box>
            </Stack>
          </Box>

          <Grid
            size={chatViewChange.column2}
            sx={{
              display: "flex",
              alignItems: "flex-end",
              height: chatViewChange.column2H,
              paddingLeft: { xs: "1rem", md: 0 },
              paddingRight: { xs: "1rem", md: 0 },
              width: "100%",
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "center",
                width: "100%",
                alignItems: "center",
                margin: 0,
                "& .MuiGrid-item": {
                  paddingLeft: { xs: "8px", sm: "16px" },
                  paddingTop: { xs: "8px", sm: "16px" },
                },
              }}
            >
              <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ width: "100%" }}>
                <div style={{ position: "relative", width: "100%" }}>
                  <div
                    ref={chatContainerRef}
                    onScroll={handleScroll}
                    onMouseEnter={() => setIsMouseInside(true)}
                    onMouseLeave={() => setIsMouseInside(false)}
                    style={{
                      height: deviceType === "mobile" ? "200px" : "200px",
                      overflowY: "auto",
                      padding: "0.5rem",
                      marginBottom: "1rem",
                      scrollbarWidth: "thin",
                      scrollbarColor: "#475569 transparent",
                      width: "100%",
                    }}
                    className="chat-container-custom"
                  >
                    <style jsx>{`
                      .chat-container-custom::-webkit-scrollbar {
                        width: 4px;
                      }
                      .chat-container-custom::-webkit-scrollbar-track {
                        background: transparent;
                      }
                      .chat-container-custom::-webkit-scrollbar-thumb {
                        background: #475569;
                        border-radius: 2px;
                      }
                      .chat-container-custom::-webkit-scrollbar-thumb:hover {
                        background: #64748b;
                      }
                    `}</style>

                    {conversations.map((conversation, index) => (
                      <div key={index}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginBottom: "0.5rem",
                          }}
                        >
                          <span
                            className="userQuestion"
                            style={{ maxWidth: "80%" }}
                          >
                            {conversation.user_text}
                          </span>
                        </div>
                        {conversation.avatar_response && (
                          <div style={{ marginBottom: "1.5rem" }}>
                            <div className="clearfix"></div>
                            <Stack
                              direction="row"
                              spacing={2}
                              sx={{ padding: 1 }}
                            >
                              <Box textAlign="left">
                                <img
                                  src={`${bucketUrl}${user?.avatar_id}.webp`}
                                  alt="avatar"
                                  width={40}
                                  style={{ padding: 5, borderRadius: 50 }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                                <div
                                  style={{
                                    display: "none",
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50px",
                                    backgroundColor: "#3b82f6",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                  }}
                                >
                                  {user?.name?.charAt(0)}
                                </div>
                              </Box>
                              <Box
                                style={{
                                  color: "#94a3b8",
                                  lineHeight: "1.5",
                                  maxWidth: "85%",
                                }}
                              >
                                {conversation.avatar_response}
                              </Box>
                            </Stack>
                          </div>
                        )}
                      </div>
                    ))}

                    {displayedText && (
                      <div style={{ marginBottom: "1.5rem" }}>
                        <div className="clearfix"></div>
                        <Stack direction="row" spacing={2} sx={{ padding: 1 }}>
                          <Box textAlign="left">
                            <img
                              src={`${bucketUrl}${user?.avatar_id}.webp`}
                              alt="avatar"
                              width={40}
                              style={{ padding: 5, borderRadius: 50 }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            <div
                              style={{
                                display: "none",
                                width: "40px",
                                height: "40px",
                                borderRadius: "50px",
                                backgroundColor: "#3b82f6",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#ffffff",
                                fontWeight: "bold",
                                fontSize: "1rem",
                              }}
                            >
                              A
                            </div>
                          </Box>
                          <Box
                            style={{
                              color: "#94a3b8",
                              lineHeight: "1.5",
                              maxWidth: "85%",
                            }}
                          ></Box>
                        </Stack>
                      </div>
                    )}
                  </div>

                  {showScrollToBottom && (
                    <IconButton
                      onClick={scrollToBottom}
                      sx={{
                        position: "absolute",
                        bottom: "0px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#3b82f6",
                        color: "#ffffff",
                        width: "36px",
                        height: "36px",
                        "&:hover": {
                          backgroundColor: "#2563eb",
                          transform: "translateX(-50%) scale(1.1)",
                        },
                        transition: "all 0.3s ease",
                        zIndex: 10,
                        boxShadow: "0 2px 8px rgba(59, 130, 246, 0.4)",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                      }}
                      size="small"
                    >
                      <KeyboardArrowDownIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  )}
                </div>

                <form className="userInputFrom" style={{ width: "100%" }}>
                  <input
                    type="text"
                    placeholder={
                      questionLimitReached
                        ? "Your question limit has reached its max. Please upgrade your plan for more."
                        : "Ask anything..."
                    }
                    value={userInput}
                    disabled={questionLimitReached}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        userInput.trim() &&
                        !questionLimitReached
                      ) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    style={{
                      width: deviceType === "mobile" ? "75%" : "87%",
                      opacity: questionLimitReached ? 0.5 : 1,
                      cursor: questionLimitReached ? "not-allowed" : "text",
                    }}
                  />
                  <button
                    type="button"
                    onClick={toggleMic}
                    disabled={questionLimitReached}
                    style={{
                      opacity: questionLimitReached ? 0.4 : 1,
                      cursor: questionLimitReached ? "not-allowed" : "pointer",
                    }}
                  >
                    {micUnmute ? (
                      <img
                        src={`/json-media/icons/mic.svg`}
                        alt="Mic On"
                        width={20}
                      />
                    ) : (
                      <img
                        src={`/json-media/icons/micmute.svg`}
                        alt="Mic Off"
                        width={20}
                      />
                    )}
                  </button>
                  {/* <button
                    type="button"
                    onClick={sdkInterrupt}
                    title="Interrupt avatar"
                    style={{
                      color: isAvatarTalking ? "#ef4444" : "#9ca3af",
                      fontSize: "20px",
                      cursor: "pointer",
                      opacity: isAvatarTalking ? 1 : 0.5,
                      transition: "all 0.2s ease",
                    }}
                  >
                    ⏹
                  </button> */}
                  {isAvatarTalking ? (
                    <IconButton
                      onClick={sdkInterrupt}
                      title="Stop generating"
                      size="small"
                      sx={{
                        color: "#ffffff",
                        "&:hover": {
                          color: "#e5e7eb",
                          transform: "scale(1.1)",
                        },
                        transition: "all 0.15s ease",
                      }}
                    >
                      <StopIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={handleSend}
                      disabled={!userInput.trim() || questionLimitReached}
                      title="Send"
                      size="small"
                      sx={{
                        color:
                          userInput.trim() && !questionLimitReached
                            ? "#ffffffff"
                            : "#64748b",
                        "&.Mui-disabled": {
                          color: "#64748b",
                        },
                        "&:hover": {
                          color:
                            userInput.trim() && !questionLimitReached
                              ? "#2563eb"
                              : "#64748b",
                        },
                        transition: "color 0.15s ease",
                      }}
                    >
                      <SendIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </form>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
